import { distanceMeters } from "./geo";

const MAPILLARY_GRAPH_URL = "https://graph.mapillary.com/images";

// Půlstrana bboxu ve stupních. 50 m ≈ 0.00045° zeměpisné šířky; mírně paddujeme.
// Mapillary vyžaduje plochu bboxu < 0.01 °², takže zůstáváme bezpečně pod limitem.
const BBOX_HALF_DEG = 0.0006;
// Maximální vzdálenost snímku od WC, aby šlo opravdu o "tohle místo".
const MAX_DISTANCE_M = 50;

export interface MapillaryImage {
  id: string;
  thumbUrl: string;
}

interface FindOptions {
  token?: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

interface GraphImage {
  id: string;
  thumb_1024_url?: string;
  geometry?: { type: "Point"; coordinates: [number, number] };
}

interface GraphResponse {
  data?: GraphImage[];
}

/** Bounding box "west,south,east,north" kolem bodu (formát Mapillary `bbox`). */
export function boundingBox(lat: number, lng: number, halfDeg = BBOX_HALF_DEG): string {
  return [lng - halfDeg, lat - halfDeg, lng + halfDeg, lat + halfDeg].join(",");
}

/**
 * Najde nejbližší Mapillary snímek (do 50 m) k zadaným souřadnicím.
 * Vrací `null` při chybějícím tokenu, žádném snímku v dosahu nebo jakékoli chybě
 * — volající pak může zobrazit keyless fallback.
 */
export async function findNearestImage(
  lat: number,
  lng: number,
  opts: FindOptions = {},
): Promise<MapillaryImage | null> {
  const token = opts.token ?? import.meta.env.VITE_MAPILLARY_TOKEN;
  if (!token) return null;

  const doFetch = opts.fetchImpl ?? fetch;
  const url =
    `${MAPILLARY_GRAPH_URL}?access_token=${encodeURIComponent(token)}` +
    `&fields=id,geometry,thumb_1024_url&bbox=${boundingBox(lat, lng)}&limit=50`;

  try {
    const res = await doFetch(url, { signal: opts.signal });
    if (!res.ok) return null;

    const body = (await res.json()) as GraphResponse;
    const images = body.data ?? [];

    let best: MapillaryImage | null = null;
    let bestDist = Infinity;
    for (const img of images) {
      if (!img.thumb_1024_url || !img.geometry) continue;
      const [imgLng, imgLat] = img.geometry.coordinates;
      const dist = distanceMeters({ lat, lng }, { lat: imgLat, lng: imgLng });
      if (dist <= MAX_DISTANCE_M && dist < bestDist) {
        bestDist = dist;
        best = { id: img.id, thumbUrl: img.thumb_1024_url };
      }
    }
    return best;
  } catch {
    return null;
  }
}
